import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Chips, ChipsChangeEvent } from 'primereact/chips'
import { classNames } from 'primereact/utils'

import { createLoadingComponent } from '../../../utils/wrapper/ConfigurableComponent/createElements'

type Props = {
  id: string
  label: string
  value?: string
  separator: string
  formik: FormikProps<any>
  isInvalid: boolean
  isRequired?: boolean
  errorMessage: JSX.Element
  wrongEmails: string[] | null
  disabled: boolean
  isLoading: boolean
}

export const EmailChips: React.FC<Props> = ({
  id,
  label,
  value,
  separator,
  formik,
  isInvalid,
  isRequired = false,
  errorMessage,
  wrongEmails,
  disabled,
  isLoading,
}) => {
  const { t } = useTranslation()
  const splitValue = value ? value.split(separator) : []

  const onChangeHandler = useCallback(
    (e: ChipsChangeEvent) => {
      const value = e.target.value
      formik.setFieldValue(id, value?.join(separator) || null)
    },
    [formik, id, separator],
  )

  const errorStyle = {
    margin: '-0.25rem -0.5rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '16px',
    paddingRight: '30px',
    marginRight: '-30px',
  }

  const renderCustomChip = (item: string) => {
    const isInvalidEmail = wrongEmails?.includes(item)

    if (isInvalidEmail) {
      return (
        <div
          className="block text-red-500 border-1 border-red-500 bg-red-50"
          style={errorStyle}
        >
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: '14px' }}
          ></i>
          <span className="ml-2">{item}</span>
        </div>
      )
    }

    return item
  }

  const labelComponent = (
    <label
      className={classNames('text-xs font-bold', {
        'p-error': isInvalid,
      })}
      htmlFor={id}
    >
      {isRequired && <span className="text-red-500 font-bold mr-1">*</span>}
      {label}
    </label>
  )

  const inputComponent = (
    <Chips
      inputId={id}
      name={id}
      value={splitValue}
      placeholder={
        disabled ? '' : t('settings.email.common.placeholder.emailExample')
      }
      separator=","
      onChange={onChangeHandler}
      disabled={disabled}
      className="block"
      itemTemplate={renderCustomChip}
      addOnBlur={true}
      allowDuplicate={false}
      pt={{
        container: { className: 'pb-2' },
        token: { className: 'mt-2' },
      }}
    />
  )

  return (
    <div className="field">
      {labelComponent}
      {isLoading ? createLoadingComponent() : inputComponent}
      {errorMessage}
    </div>
  )
}
